<template>
  <el-table
    v-el-table-infinite-scroll="loadTableData"
    :row-class-name="
      (data) => (isMe(data.row.ownerPlayer.userAddress) || isMe(data.row.joinerPlayer.userAddress) ? 'tr-own' : '')
    "
    :data="tableData"
    class="bets-stats-table"
    table-layout="auto"
    :show-header="false"
    :height="$device.isMobile ? '100%' : '360'"
    :infinite-scroll-disabled="false"
    style="--el-table-bg-color: none"
  >
    <template #empty>
      <div v-if="isLoading && allTableData.length === 0" style="text-align: center; width: 100%">
        {{ $t('coreLoading') }}...
      </div>
    </template>
    <el-table-column prop="ownerPlayer" label="ownerPlayer">
      <template #default="scope">
        <div class="date-closed">
          <NuxtTime locale="en-GB" :datetime="scope.row.closedAt" month="numeric" day="numeric" year="numeric" />
        </div>

        <div class="user-info user-left-side">
          <div class="avatar-wrapper">
            <el-avatar
              class="avatar"
              :class="{ own: isMe(getUserData(scope.row, true)?.userAddress || ZeroAddress) }"
              :size="49"
              :src="getUserData(scope.row, true)?.userAvatarUrl"
            />
            <img class="coin" :src="scope.row.isOddBet ? '/img/icons/flipCoinChest.png' : '/img/icons/flipCoin.png'" />
          </div>

          <div class="user-info-wrapper" :style="{ marginTop: $device.isMobile ? '-16px' : '0' }">
            <div class="user-name-block">
              <div class="user-name" :class="{ own: isMe(getUserData(scope.row, true)?.userAddress || ZeroAddress) }">
                {{
                  getUserData(scope.row, true)?.userAddress?.includes(getUserData(scope.row, true)?.userName || '')
                    ? formatAddress(getUserData(scope.row, true)?.userAddress)
                    : getUserData(scope.row, true)?.userName
                }}
              </div>

              <TavernFlipCoinStatsTableRewardBlock
                :row-scope="scope.row"
                :user-data="getUserData(scope.row, true)"
                :is-winner="true"
              />
            </div>
          </div>
        </div>

        <div v-if="$device.isMobile" class="user-info user-right-side">
          <div class="avatar-wrapper">
            <el-avatar
              class="avatar"
              :class="{ own: isMe(getUserData(scope.row, false)?.userAddress || ZeroAddress) }"
              :size="49"
              :src="getUserData(scope.row)?.userAvatarUrl"
            />
            <img class="coin" :src="scope.row.isOddBet ? '/img/icons/flipCoin.png' : '/img/icons/flipCoinChest.png'" />
          </div>

          <div class="user-info-wrapper">
            <div class="user-name-block">
              <div class="user-name" :class="{ own: isMe(getUserData(scope.row, false)?.userAddress || ZeroAddress) }">
                {{
                  getUserData(scope.row, false)?.userAddress?.includes(getUserData(scope.row, false)?.userName || '')
                    ? formatAddress(getUserData(scope.row, false)?.userAddress)
                    : getUserData(scope.row, false)?.userName
                }}
              </div>

              <TavernFlipCoinStatsTableRewardBlock
                :row-scope="scope.row"
                :user-data="getUserData(scope.row, false)"
                :is-winner="false"
              />
            </div>
          </div>
          <img class="eye" src="/img/icons/flipCoinEye.png" @click="startAnimation(scope.row)" />
        </div>
      </template>
    </el-table-column>
    <el-table-column v-if="!$device.isMobile" prop="joinerPlayer" label="joinerPlayer" min-width="400">
      <template #default="scope">
        <div class="user-info user-right-side">
          <div class="avatar-wrapper">
            <el-avatar
              class="avatar"
              :class="{ own: isMe(getUserData(scope.row, false)?.userAddress || ZeroAddress) }"
              :size="49"
              :src="getUserData(scope.row)?.userAvatarUrl"
            />
            <img class="coin" :src="scope.row.isOddBet ? '/img/icons/flipCoin.png' : '/img/icons/flipCoinChest.png'" />
          </div>

          <div class="user-info-wrapper">
            <div class="user-name-block">
              <div class="user-name" :class="{ own: isMe(getUserData(scope.row, false)?.userAddress || ZeroAddress) }">
                {{
                  getUserData(scope.row, false)?.userAddress?.includes(getUserData(scope.row, false)?.userName || '')
                    ? formatAddress(getUserData(scope.row, false)?.userAddress)
                    : getUserData(scope.row, false)?.userName
                }}
              </div>

              <TavernFlipCoinStatsTableRewardBlock
                :row-scope="scope.row"
                :user-data="getUserData(scope.row, false)"
                :is-winner="false"
              />
            </div>
          </div>
          <img class="eye" src="/img/icons/flipCoinEye.png" @click="startAnimation(scope.row)" />
        </div>
      </template>
    </el-table-column>
  </el-table>
</template>

<script setup lang="ts">
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import { getAddress, ZeroAddress, isAddress } from 'ethers';
import { GameStatus, type FlipCoinGame, type GetPageOfGamesParams } from '~/types/FlipCoin';
import { default as vElTableInfiniteScroll } from 'el-table-infinite-scroll';
import { apiUrls } from '~/utils/constants';
const { address } = useWeb3ModalAccount();
const { apiUrl } = useEnvs();
const allTableData = ref<FlipCoinGame[]>([]);
const offset = ref(0);
const limit = ref(50);
const isLoading = ref(false);
const noMoreData = ref(false);
const sortParam: string = 'closedAt:desc';

const showOwn = inject<Ref<boolean>>('showOwn', ref(false));

const emit = defineEmits(['start-flipcoin-animation']);

const isMe = (betAddress: string) => {
  return (
    betAddress &&
    isAddress(betAddress) &&
    address.value &&
    isAddress(address.value) &&
    getAddress(address.value) === getAddress(betAddress)
  );
};

const startAnimation = (game: FlipCoinGame) => {
  emit('start-flipcoin-animation', game);
};

const tableData = computed(() => allTableData.value);

watch(showOwn, async (newValue) => {
  if (isLoading.value) return;

  isLoading.value = true;
  offset.value = 0;
  allTableData.value = [];

  try {
    let data: FlipCoinGame[];

    if (newValue) {
      data = await getPageOfGames({
        sort: sortParam,
        userAddress: address.value ? getAddress(address.value) : undefined
      });
      noMoreData.value = true;
    } else {
      data = await getPageOfGames({
        sort: sortParam,
        limit: limit.value,
        offset: offset.value
      });
      noMoreData.value = false;
    }

    allTableData.value = data;
  } finally {
    isLoading.value = false;
  }
});

const loadTableData = async () => {
  if (isLoading.value || noMoreData.value) return;
  isLoading.value = true;

  const params: GetPageOfGamesParams = {
    limit: limit.value,
    offset: offset.value,
    sort: sortParam
  };

  if (showOwn?.value && address.value) {
    params.userAddress = address.value;
  }

  const data = await getPageOfGames(params);
  if (data.length) {
    allTableData.value = [...allTableData.value, ...data];
    offset.value += limit.value;
  }

  if (data.length < limit.value) {
    noMoreData.value = true;
  }

  isLoading.value = false;
};

const getPageOfGames = async (params: GetPageOfGamesParams): Promise<FlipCoinGame[]> => {
  try {
    const url = `${apiUrl}${apiUrls.flipCoinGame.games}`;
    const queryObj: Record<string, string> = {
      status: GameStatus.COMPLETED,
      sort: params.sort
    };

    if (typeof params.offset !== 'undefined') {
      queryObj.offset = params.offset.toString();
    }

    if (typeof params.limit !== 'undefined') {
      queryObj.limit = params.limit.toString();
    }

    if (params.userAddress) {
      queryObj.userAddress = params.userAddress;
    }

    const query = new URLSearchParams(queryObj).toString();

    const response = await $fetch<{ items: FlipCoinGame[] }>(`${url}?${query}`);

    return response.items;
  } catch (error) {
    console.error('Error loading games:', error);
    return [];
  }
};
</script>

<style lang="scss" scoped>
.avatar-wrapper {
  position: relative;
  width: 75px;

  img {
    width: 32px;
    position: absolute;
    top: 7px;
    right: 7px;
  }

  .avatar {
    border: 2px solid #201a0e;

    &.own {
      border-color: #fff;
    }
  }
}

.eye {
  position: absolute;
  right: 20px;
  top: 15px;
  width: 25px;
  height: 17px;
  cursor: pointer;
}

.date-closed {
  font-family: Eczar;
  font-weight: 600;
  font-size: 14px;
  line-height: 15.4px;
  color: #a2a2a2;
  margin-bottom: 1px;
}

.user-name {
  font-family: Eczar;
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  color: #a2a2a2;
  padding-bottom: 7px;
  width: 130px;
  text-overflow: ellipsis;
  overflow-x: hidden;
  white-space: nowrap;

  &.own {
    color: #fff;
  }
}

.user-info {
  display: flex;
  gap: 10px;
}

.user-info.user-right-side {
  margin-top: 16px;
}

.user-info-wrapper {
  display: flex;
  align-items: center;
}

.user-name-block {
  margin-right: 20px;
}
</style>
<style lang="scss">
.bets-stats-table.el-table {
  user-select: none;
  margin-bottom: 10px;
  max-height: 405px;

  @media screen and (max-width: 479px) {
    width: 99vw;
    max-width: 100vw;
    margin-left: -25px;
    max-height: 100%;
  }

  .el-table__empty-text {
    font-size: 24px;
  }

  .cell {
    height: 68px;
    padding: 0;

    @media screen and (max-width: 479px) {
      height: auto;
    }
  }

  table {
    border-spacing: 0px 8px;
  }

  /* For correct box-shadow effect on hover tr */
  .el-table__body {
    padding-left: 5px;
  }

  tr {
    background: linear-gradient(to bottom, rgba(78, 49, 39, 1), rgba(0, 0, 0, 1));
    border-radius: 12px;
  }

  td.el-table__cell {
    border-top: 1px solid #b87d2b33;
    border-bottom: 1px solid #b87d2b33;

    padding: 8px 0 10px;
  }

  td.el-table__cell:first-child {
    border-left: 1px solid #b87d2b33;
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
    padding-left: 18px;
  }

  td.el-table__cell:last-child {
    border-right: 1px solid #b87d2b33;
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
    padding-right: 18px;
  }

  .tr-own {
    background: linear-gradient(180deg, rgba(221, 177, 104, 1) 0%, rgba(135, 70, 24, 1) 50%, rgba(12, 4, 5, 1) 100%);
    box-shadow: 0 0 1px 0 #ddcead;

    .date-closed {
      color: #fff;
    }

    td.el-table__cell {
      border-color: #b87d2b;
    }
  }

  :deep(.el-table__cell) {
    background-color: unset;
  }
}

.el-table--enable-row-hover .el-table__body tr:hover > td.el-table__cell {
  background-color: unset !important;
}

.el-table__inner-wrapper:before {
  display: none;
}
</style>
